import React from 'react';
import { View, StyleSheet } from 'react-native';
import { Text } from '@/components/common/text';
import { useThemedStyles } from '@/hooks/shared/useThemeColors';
import { useTransactionPalette } from '@/hooks/admin/useTransactionPalette';
import { GlassCard } from '@/components/shared/redesign';
import { formatCurrency } from '@/utils/common/formatting';
import { ITransactionSummary } from '@/types/admin/transactions';

interface TransactionSummaryProps {
  summary: ITransactionSummary;
}

export const TransactionSummary: React.FC<TransactionSummaryProps> = ({ summary }) => {
  const styles = useStyles();
  const palette = useTransactionPalette();

  const getBalanceColor = (balance: number) => {
    if (balance > 0) return palette.charge;
    if (balance < 0) return palette.payment;
    return palette.voided;
  };

  return (
    <View style={styles.container}>
      <GlassCard style={styles.summaryCardSpacing} padding={12}>
        <Text style={styles.label}>Total Charges</Text>
        <Text style={[styles.value, { color: palette.charge }]}>
          {formatCurrency(summary.totalCharges)}
        </Text>
      </GlassCard>

      <GlassCard style={styles.summaryCardSpacing} padding={12}>
        <Text style={styles.label}>Total Payments</Text>
        <Text style={[styles.value, { color: palette.payment }]}>
          {formatCurrency(summary.totalPayments)}
        </Text>
      </GlassCard>

      <GlassCard style={styles.summaryCardSpacing} padding={12}>
        <Text style={styles.label}>Current Balance</Text>
        <Text style={[styles.value, { color: getBalanceColor(summary.currentBalance) }]}>
          {formatCurrency(Math.abs(summary.currentBalance))}
          {summary.currentBalance < 0 && ' (Credit)'}
        </Text>
      </GlassCard>
    </View>
  );
};

const useStyles = () =>
  useThemedStyles(({ components, colors }) =>
    StyleSheet.create({
      container: {
        flexDirection: 'row',
        justifyContent: 'space-between',
        paddingVertical: 12,
        gap: 12,
      },
      summaryCardSpacing: {
        flex: 1,
      },
      label: {
        fontSize: 11,
        fontWeight: '600',
        color: components.admin.dataCell.textMuted,
        marginBottom: 4,
        textTransform: 'uppercase',
        letterSpacing: 0.5,
      },
      value: {
        fontSize: 16,
        fontWeight: '700',
        color: colors.text.primary,
      },
    }),
  );
